import { FC, ReactNode } from 'react';
import { captureException } from '@sentry/react';
import { Tag as AntTag, Avatar, Button, Modal, Space } from 'antd';
import { Plus, Tag as TagIcon } from 'lucide-react';
import {
    TbArchive,
    TbCrosshair,
    TbDotsVertical,
    TbEdit,
    TbEye,
    TbLock,
    TbLockOpen,
    TbStar,
    TbStarOff,
    TbTrash,
} from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useDeleteTag, useDeleteWatchlist, useUpdateWatchlist } from '@/queries';
import { VirtualizedList as List } from '@/shared';
import {
    WorkspaceResourceActionsDropdown,
    type WorkspaceResourceActionMenuItem,
} from '@/shared/components/dropdowns/WorkspaceResourceDropdowns';
import { CompactCollapsible } from '@/shared/components/layouts/sections';
import { Watchlist, WatchlistInput, Tag as WorkspaceTag } from '@/types';

import { isWorkspaceTag, PrimitiveKind, PrimitiveRecord } from '../../types';

interface PrimitiveListItemProps {
    children?: ReactNode;
    kind: PrimitiveKind;
    nested?: boolean;
    open?: boolean;
    record: PrimitiveRecord;
    onAddSubtag: (tag: WorkspaceTag) => void;
    onEdit: (record: PrimitiveRecord) => void;
    onOpenChange?: (open: boolean) => void;
}

const PrimitiveListItem: FC<PrimitiveListItemProps> = ({
    children,
    kind,
    nested,
    onAddSubtag,
    onEdit,
    onOpenChange,
    open = false,
    record,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const deleteTag = useDeleteTag();
    const deleteWatchlist = useDeleteWatchlist();
    const updateWatchlist = useUpdateWatchlist();
    const tag = isWorkspaceTag(record) ? record : undefined;
    const watchlist: Watchlist | undefined = isWorkspaceTag(record) ? undefined : record;
    const deleting = deleteTag.isPending || deleteWatchlist.isPending;
    const mutating = deleting || updateWatchlist.isPending;

    const remove = async () => {
        try {
            if (tag) {
                await deleteTag.mutateAsync(tag.id);
            } else if (watchlist) {
                await deleteWatchlist.mutateAsync(watchlist.id);
            }

            toast.success(`${kind === 'tags' ? 'Tag' : 'Watchlist'} deleted.`);
        } catch (error) {
            captureException(error);
            toast.error(`Unable to delete this ${kind === 'tags' ? 'tag' : 'watchlist'}.`);
        }
    };

    const updateWatchlistState = async (input: Partial<WatchlistInput>) => {
        if (!watchlist || updateWatchlist.isPending) {
            return;
        }

        try {
            await updateWatchlist.mutateAsync({ id: watchlist.id, input });
        } catch (error) {
            captureException(error);
            toast.error('Unable to update this watchlist.');
        }
    };

    const title = tag?.name ?? watchlist?.title ?? 'Untitled';
    const description = tag
        ? `${tag.assignmentCount} ${tag.assignmentCount === 1 ? 'attachment' : 'attachments'}`
        : watchlist?.description || `${watchlist?.itemCount ?? 0} tracked items`;
    const icon: ReactNode = tag ? (
        <TagIcon size='0.75rem' color={tag.color ?? token.colorTextSecondary} />
    ) : (
        <TbCrosshair size='0.85rem' style={{ color: token.colorTextSecondary }} />
    );

    const confirmRemove = () => {
        Modal.confirm({
            cancelText: 'Cancel',
            centered: true,
            content: `This permanently deletes “${title}”.`,
            okButtonProps: { danger: true, loading: deleting },
            okText: 'Delete',
            title: `Delete ${kind === 'tags' ? 'Tag' : 'Watchlist'}?`,
            onOk: remove,
        });
    };
    const actions: WorkspaceResourceActionMenuItem[] = [
        ...(tag && !tag.parentId
            ? [
                  {
                      icon: (
                          <Space size={2}>
                              <TagIcon size='0.875rem' />
                              <Plus size='0.75rem' />
                          </Space>
                      ),
                      key: 'addSubtag',
                      label: 'Add Subtag',
                      section: 'primary',
                      onClick: () => onAddSubtag(tag),
                  },
              ]
            : []),
        ...(watchlist
            ? [
                  {
                      icon: watchlist.favorited ? <TbStarOff size='1rem' /> : <TbStar size='1rem' />,
                      key: 'favorited',
                      label: watchlist.favorited ? 'Remove from Favorites' : 'Add to Favorites',
                      onClick: () => void updateWatchlistState({ favorited: !watchlist.favorited }),
                  },
                  {
                      icon: watchlist.locked ? <TbLockOpen size='1rem' /> : <TbLock size='1rem' />,
                      key: 'locked',
                      label: watchlist.locked ? 'Unlock' : 'Lock',
                      onClick: () => void updateWatchlistState({ locked: !watchlist.locked }),
                  },
                  {
                      icon: watchlist.archived ? <TbEye size='1rem' /> : <TbArchive size='1rem' />,
                      key: 'archived',
                      label: watchlist.archived ? 'Restore' : 'Archive',
                      onClick: () => void updateWatchlistState({ archived: !watchlist.archived }),
                  },
              ]
            : []),
        {
            icon: <TbEdit size='1rem' />,
            key: 'edit',
            label: 'Edit',
            onClick: () => onEdit(record),
        },
        {
            danger: true,
            icon: <TbTrash size='1rem' />,
            key: 'delete',
            label: 'Delete',
            onClick: confirmRemove,
        },
    ];
    const actionsControl = (
        <Space.Compact size='small'>
            <Button disabled={!record.canEdit || mutating} size='small' onClick={() => onEdit(record)}>
                Edit
            </Button>
            <WorkspaceResourceActionsDropdown
                items={actions}
                loading={mutating}
                resourceLabel={kind === 'tags' ? 'Tag' : 'Watchlist'}
            >
                <Button
                    aria-label={`More actions for ${title}`}
                    disabled={!record.canEdit || mutating}
                    icon={<TbDotsVertical size='1rem' style={{ color: token.colorTextTertiary }} />}
                    loading={mutating}
                    size='small'
                />
            </WorkspaceResourceActionsDropdown>
        </Space.Compact>
    );
    const titleNode = (
        <Space size='small'>
            <span>{title}</span>
            <AntTag bordered={false}>{record.visibility}</AntTag>
        </Space>
    );

    if (children) {
        return (
            <CompactCollapsible
                contentStyle={{ padding: 0 }}
                headerExtra={actionsControl}
                headerStyle={{ padding: '0.375rem 0' }}
                icon={
                    <Avatar
                        icon={icon}
                        shape='square'
                        size={20}
                        style={{ border: `1px solid ${token.colorBorder}` }}
                    />
                }
                label={
                    <div>
                        {titleNode}
                        <div>
                            <small style={{ color: token.colorTextTertiary }}>{description}</small>
                        </div>
                    </div>
                }
                open={open}
                sectionStyle={{ borderTop: 0 }}
                onOpenChange={(nextOpen) => onOpenChange?.(nextOpen)}
            >
                {children}
            </CompactCollapsible>
        );
    }

    return (
        <List.Item style={{ paddingLeft: nested ? '1.5rem' : 0, paddingRight: 0 }}>
            <List.Item.Meta
                avatar={
                    <Avatar icon={icon} shape='square' size={20} style={{ border: `1px solid ${token.colorBorder}` }} />
                }
                description={<small>{description}</small>}
                title={titleNode}
            />
            {actionsControl}
        </List.Item>
    );
};

export default PrimitiveListItem;
